import { useEffect, useState } from 'react';
import { cloneDeep } from 'lodash';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'material-react-toastify';
import { Category, getEmptyCategory } from '../../interfaces/Category/Category';
import { sharedStyles } from '../../utils/Style';
import { Waiting } from '../../components/Waiting/Waiting';
import {
  createCategory,
  updateCategory,
  updateCategoryMedia,
} from '../../api/Category';
import { CategoryMediaOptions } from '../../interfaces/Category/CategoryMediaOptions';
import { ImageInput } from '../../components/ImageInput/ImageInput';
import { MetaCard } from '../../components/Meta/MetaCard';
import { MetaTable } from '../../interfaces/Meta/MetaTable';
import { OpenInFrontend } from '../../components/OpenInFrontend/OpenInFrontend';
import { TranslationAdornment } from '../../components/Translation/TranslationAdornment';

interface ModifyCategorySectionProps {
  category: Category;
  categories: Category[];
  onModify: (id: number) => void;
  onCancel: () => void;
}

export const ModifyCategorySection = (props: ModifyCategorySectionProps) => {
  const [currentState, setCurrentState] = useState(getEmptyCategory());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setCurrentState(cloneDeep(props.category));
  }, [props.category]);

  const isEditing = !!currentState.id;

  const handleSave = () => {
    setLoading(true);

    const mutation = isEditing ? updateCategory : createCategory;
    mutation(currentState)
      .then((c) => {
        toast.success('Değişiklikler başarıyla kaydedildi');
        props.onModify(c.id);
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  const handleUpdateMedia = (options: CategoryMediaOptions) => {
    setLoading(true);
    updateCategoryMedia(currentState.id, options)
      .then((c) => {
        toast.success('Medya başarıyla güncellendi');
        props.onModify(c.id);
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Waiting open={loading} />
      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: 400,
          height: 'fit-content',
          p: 3,
          gap: 1.5,
        }}
      >
        <Typography sx={{ ...sharedStyles.h5, fontWeight: 'bold' }}>
          {isEditing ? 'Kategoriyi Güncelle' : 'Yeni Kategori'}
        </Typography>
        <TextField
          label='İsim'
          variant='outlined'
          size='small'
          value={currentState.name}
          onChange={(e) =>
            setCurrentState({ ...currentState, name: e.target.value })
          }
          fullWidth
          InputProps={{
            endAdornment: (
              <TranslationAdornment
                defaultValue={currentState.name}
                value={currentState.nameTranslation}
                onChange={(x) =>
                  setCurrentState({ ...currentState, nameTranslation: x })
                }
              />
            ),
          }}
        />

        {!!currentState.parentID && (
          <TextField
            label='Üst Kategori'
            variant='outlined'
            size='small'
            value={
              props.categories.find((c) => c.id === currentState.parentID)
                ?.name ?? ''
            }
            disabled
            fullWidth
          />
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexGrow: 1,
            alignItems: 'center',
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={currentState.isActive}
                onChange={() =>
                  setCurrentState({
                    ...currentState,
                    isActive: !currentState.isActive,
                  })
                }
              />
            }
            label={<Typography sx={sharedStyles.subtitle2}>Aktif</Typography>}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={currentState.isSearchable}
                onChange={() =>
                  setCurrentState({
                    ...currentState,
                    isSearchable: !currentState.isSearchable,
                  })
                }
              />
            }
            label={
              <Typography sx={sharedStyles.subtitle2}>Aranabilir</Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={currentState.isPopular}
                onChange={() =>
                  setCurrentState({
                    ...currentState,
                    isPopular: !currentState.isPopular,
                  })
                }
              />
            }
            label={<Typography sx={sharedStyles.subtitle2}>Popüler</Typography>}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexGrow: 1,
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          {isEditing && (
            <OpenInFrontend
              variant='outlined'
              path='kategori'
              slug={currentState.slug}
              isActive={currentState.isActive}
            />
          )}

          <Box sx={{ flexGrow: 1 }} />

          <Button
            color='inherit'
            variant='contained'
            onClick={props.onCancel}
            sx={{ ...sharedStyles.buttonText }}
          >
            İptal
          </Button>

          <Button
            variant='contained'
            onClick={handleSave}
            sx={{ ...sharedStyles.buttonText }}
          >
            Kaydet
          </Button>
        </Box>
      </Paper>
      {isEditing && (
        <>
          <MetaCard
            key={currentState.id}
            table={MetaTable.CATEGORY}
            id={currentState.id}
          />

          <ImageInput
            title='Logo'
            existingImage={currentState.logo}
            onUpload={(file) => handleUpdateMedia({ logo: file })}
            onDelete={() => handleUpdateMedia({ eraseLogo: true })}
          />

          <ImageInput
            title='Afiş'
            existingImage={currentState.banner}
            onUpload={(file) => handleUpdateMedia({ banner: file })}
            onDelete={() => handleUpdateMedia({ eraseBanner: true })}
          />
        </>
      )}
    </Box>
  );
};
